import { useId } from "react";
import { TriangleAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import type { User } from "@/services/api/users";

interface DeleteUserDialogProps {
  open: boolean;
  user: User | null;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function DeleteUserDialog({
  open,
  user,
  isLoading,
  onOpenChange,
  onConfirm,
}: DeleteUserDialogProps) {
  const descriptionId = useId();
  const isRegistered = !!user?.entraObjectId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={descriptionId}>
        <DialogHeader>
          <DialogTitle>Delete user</DialogTitle>
          <DialogDescription id={descriptionId}>
            Are you sure you want to delete{" "}
            <span className="font-medium text-ink-900">
              {user?.email ?? "this user"}
            </span>
            ? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          {isRegistered ? (
            <p>
              This user is registered in{" "}
              <span className="font-medium">Microsoft Entra</span>. Deleting
              them here will only remove them from this application. You must
              also remove or disable the user in{" "}
              <span className="font-medium">MS Entra</span> separately to fully
              revoke access.
            </p>
          ) : (
            <p>
              This user may already exist in{" "}
              <span className="font-medium">Microsoft Entra</span>. Deleting
              them here will only remove them from this application. If the user
              exists in <span className="font-medium">MS Entra</span>, you must
              also remove or disable them there separately to fully revoke
              access.
            </p>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
